import { motion } from 'framer-motion';
import { Bot, Play, Search } from 'lucide-react';
import React, { useState } from 'react';

import { BrowserAgentViewer } from '@/components/browser-use-scraper';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export const ZeyomPage: React.FC = () => {
  const [workflowId, setWorkflowId] = useState('');
  const [activeWorkflowId, setActiveWorkflowId] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = workflowId.trim();
    if (trimmed) {
      setActiveWorkflowId(trimmed);
    }
  };

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col bg-background">
      {/* Header */}
      <div className="shrink-0 border-b px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600 to-blue-600 shadow-lg shadow-violet-600/20">
            <Bot className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Browser Agent Viewer</h1>
            <p className="text-xs text-muted-foreground">
              Watch browser-use scraper runs in real-time
            </p>
          </div>
        </div>
      </div>

      {/* Workflow ID input */}
      <div className="shrink-0 border-b px-6 py-3">
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={workflowId}
              onChange={(e) => setWorkflowId(e.target.value)}
              placeholder="Enter Temporal workflow ID…"
              className="h-9 pl-9 font-mono text-sm"
            />
          </div>
          <Button
            type="submit"
            size="sm"
            disabled={!workflowId.trim()}
            className={cn(
              'h-9 gap-1.5 bg-gradient-to-r from-violet-600 to-blue-600 text-white shadow-lg shadow-violet-600/20 transition-all hover:shadow-violet-600/30',
              !workflowId.trim() && 'opacity-50',
            )}
          >
            <Play className="h-3.5 w-3.5" />
            Load
          </Button>
        </form>
      </div>

      {/* Main content */}
      <div className="flex-1 overflow-hidden p-4">
        {activeWorkflowId ? (
          <motion.div
            key={activeWorkflowId}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="h-full"
          >
            <BrowserAgentViewer workflowId={activeWorkflowId} />
          </motion.div>
        ) : (
          <div className="flex h-full items-center justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center gap-4 text-center"
            >
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl border bg-muted">
                <Bot className="h-8 w-8 text-muted-foreground stroke-[1.5]" />
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">No run loaded</p>
                <p className="mt-1 text-xs text-muted-foreground/60">
                  Enter a workflow ID above to view the browser agent timeline
                </p>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};
